import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CreditCardIcon, StethoscopeIcon, CalendarIcon, MapPinIcon, XIcon } from 'lucide-react';
import { NearestLocationFinder } from './NearestLocationFinder';
import { ShopFramesModal } from './ShopFramesModal';
import { ShoppingBagIcon } from 'lucide-react';
const HERO_IMAGES = [{
  url: 'https://cdn.magicpatterns.com/uploads/aFvhi59AUt9XaBm9KJnaSq/ChatGPT_Image_Mar_10,_2026,_12_58_25_PM.png',
  alt: 'Doctor performing an eye examination with a phoropter'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/84bc8PQYBU8anszJ8ArQu7/Hero_Image_1.png',
  alt: 'Two children smiling and trying on stylish glasses'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/nCAbbYRByihxziefw84Cpw/ChatGPT_Image_Mar_10,_2026,_01_40_06_PM.png',
  alt: 'Elegant gold-rimmed glasses with a warm glowing light effect'
}];
const BOOKING_URL = 'https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0';
interface HeroSectionProps {
  headline?: string;
  subheadline?: string;
}
export function HeroSection({
  headline,
  subheadline
}: HeroSectionProps) {
  const [currentImage, setCurrentImage] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [activeLinkIndex, setActiveLinkIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);
  const quickLinks = [{
    title: 'Shop Frames',
    subtitle: 'Browse online',
    icon: ShoppingBagIcon,
    href: '#',
    isShopModal: true
  }, {
    title: 'Make a Payment',
    subtitle: 'Pay online securely',
    icon: CreditCardIcon,
    href: '/payments'
  }, {
    title: 'Online Booking',
    subtitle: 'Schedule anytime',
    icon: CalendarIcon,
    href: '/contact'
  }, {
    title: 'Find Locations',
    subtitle: '6 MD locations',
    icon: MapPinIcon,
    href: '#',
    isModal: true
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = scrollContainerRef.current;
    // Calculate which item is most visible
    const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
    // Avoid NaN if not scrollable
    if (isNaN(scrollPercentage)) return;
    const index = Math.round(scrollPercentage * (quickLinks.length - 1));
    setActiveLinkIndex(Math.min(Math.max(index, 0), quickLinks.length - 1));
  };
  return <section id="home" className="relative min-h-[70vh] md:min-h-[70vh] flex flex-col justify-center pt-24 md:pt-40 pb-36 md:pb-36 overflow-hidden bg-brand-dark" data-id="element-316">
      {/* Background Image Slider */}
      <AnimatePresence mode="sync" data-id="element-317">
        <motion.div key={currentImage} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.2,
        ease: 'easeInOut'
      }} className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('${HERO_IMAGES[currentImage].url}')`
      }} role="img" aria-label={HERO_IMAGES[currentImage].alt} data-id="element-318" />
      </AnimatePresence>

      {/* Overlay Gradients — lighter on the right to show images */}
      <div className="absolute inset-0 z-[1] bg-brand-dark/50 md:hidden" data-id="element-319" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-brand-dark via-brand-dark/60 to-transparent hidden md:block" data-id="element-320" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-brand-dark/80 via-transparent to-brand-dark/20" data-id="element-321" />

      {/* Slide Indicators */}
      <div className="hidden md:flex absolute bottom-[120px] right-4 sm:right-8 lg:right-12 z-20 gap-2" data-id="element-322">
        {HERO_IMAGES.map((_, index) => <button key={index} onClick={() => setCurrentImage(index)} className={`w-2.5 h-2.5 rounded-full transition-all duration-500 focus-ring ${index === currentImage ? 'bg-brand-gold w-8' : 'bg-white/40 hover:bg-white/60'}`} aria-label={`Show slide ${index + 1}`} data-id="element-323" />)}
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 w-full flex-grow flex flex-col justify-center" data-id="element-324">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="w-full lg:w-3/5 max-w-2xl" data-id="element-325">
          <h1 className="text-hero-mobile md:text-hero text-white mb-6 font-heading font-bold leading-tight drop-shadow-lg" data-id="element-326">
            {headline || 'Welcome To Visual Eyes Group!'}
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-10 font-body leading-relaxed drop-shadow-md" data-id="element-327">
            {subheadline || 'We believe that all people should have access to first-class eye care. Our team works tirelessly to ensure that healthy eyes is a given for all people, all the time.'}
          </p>

          <div className="flex flex-row gap-3 sm:gap-4" data-id="element-328">
            <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="bg-brand-gold text-white px-4 sm:px-8 py-3 sm:py-4 rounded-button font-heading font-semibold text-xs sm:text-base text-center hover:bg-brand-gold-light transition-colors shadow-lg focus-ring flex-1 sm:flex-none whitespace-nowrap" data-id="element-329">
              Book Appointment
            </a>
            <Link to="/services" className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-button font-heading font-semibold text-xs sm:text-base text-center hover:bg-white/20 hover:border-white/50 transition-colors focus-ring flex-1 sm:flex-none whitespace-nowrap" data-id="element-330">
              Learn More
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Quick Links - Bottom Horizontal Strip */}
      <div className="absolute bottom-0 left-0 right-0 z-30 w-full bg-brand-dark/40 backdrop-blur-xl border-t border-white/10 pb-4 md:pb-0" data-id="element-331">
        <div className="max-w-7xl mx-auto relative" data-id="element-332">
          <motion.div ref={scrollContainerRef} onScroll={handleScroll} variants={containerVariants} initial="hidden" animate="visible" className="flex md:grid md:grid-cols-4 overflow-x-auto md:overflow-visible scrollbar-hide divide-x divide-white/10 snap-x snap-mandatory" data-id="element-333">
            {quickLinks.map(link => {
            const Icon = link.icon;
            return <motion.div key={link.title} variants={itemVariants} className="h-full flex-shrink-0 w-[85%] sm:w-[45%] md:w-auto snap-start" data-id="element-334">
                  <motion.div whileHover={{
                scale: 1.02,
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }} whileTap={{
                scale: 0.98
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 17
              }} className="h-full" data-id="element-335">
                    {link.isModal ? <button onClick={() => setShowLocationModal(true)} className="flex items-center gap-3 px-4 py-6 md:flex-row md:items-start md:gap-4 md:p-8 h-full w-full focus-ring group text-left" data-id="element-336">
                        <div className="flex-shrink-0" data-id="element-337">
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-brand-gold group-hover:text-brand-gold-light transition-colors drop-shadow-md" data-id="element-338" />
                        </div>
                        <div className="text-left" data-id="element-339">
                          <h3 className="font-heading font-semibold text-white text-[16px] md:text-[18px] leading-tight mb-0.5 md:mb-1" data-id="element-340">
                            {link.title}
                          </h3>
                          <p className="text-white/50 text-[14px] font-medium whitespace-nowrap" data-id="element-341">
                            {link.subtitle}
                          </p>
                        </div>
                      </button> : link.isShopModal ? <button onClick={() => setShowShopModal(true)} className="flex items-center gap-3 px-4 py-6 md:flex-row md:items-start md:gap-4 md:p-8 h-full w-full focus-ring group text-left" data-id="element-342">
                        <div className="flex-shrink-0" data-id="element-343">
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-brand-gold group-hover:text-brand-gold-light transition-colors drop-shadow-md" data-id="element-344" />
                        </div>
                        <div className="text-left" data-id="element-345">
                          <h3 className="font-heading font-semibold text-white text-[16px] md:text-[18px] leading-tight mb-0.5 md:mb-1" data-id="element-346">
                            {link.title}
                          </h3>
                          <p className="text-white/50 text-[14px] font-medium whitespace-nowrap" data-id="element-347">
                            {link.subtitle}
                          </p>
                        </div>
                      </button> : <Link to={link.href} className="flex items-center gap-3 px-4 py-6 md:flex-row md:items-start md:gap-4 md:p-8 h-full focus-ring group" data-id="element-348">
                        <div className="flex-shrink-0" data-id="element-349">
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-brand-gold group-hover:text-brand-gold-light transition-colors drop-shadow-md" data-id="element-350" />
                        </div>
                        <div className="text-left" data-id="element-351">
                          <h3 className="font-heading font-semibold text-white text-[16px] md:text-[18px] leading-tight mb-0.5 md:mb-1" data-id="element-352">
                            {link.title}
                          </h3>
                          <p className="text-white/50 text-[14px] font-medium whitespace-nowrap" data-id="element-353">
                            {link.subtitle}
                          </p>
                        </div>
                      </Link>}
                  </motion.div>
                </motion.div>;
          })}
          </motion.div>

          {/* Mobile Scroll Indicator - Continuous Bar */}
          <div className="md:hidden absolute bottom-1 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-white/15 pointer-events-none overflow-hidden" data-id="element-354">
            <div className="h-full rounded-full bg-brand-gold transition-transform duration-200 ease-out" style={{
            width: `${100 / quickLinks.length}%`,
            transform: `translateX(${activeLinkIndex * 100}%)`
          }} data-id="element-355" />
          </div>
        </div>
      </div>

      {/* Location Finder Modal */}
      <AnimatePresence data-id="element-356">
        {showLocationModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowLocationModal(false)} data-id="element-357">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-brand-dark/60 backdrop-blur-md" data-id="element-358" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative z-10 bg-white/85 backdrop-blur-2xl border border-white/40 rounded-card shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto [&>section]:bg-transparent [&>section]:border-0 [&>section]:py-8 [&>section]:md\\:py-10" onClick={e => e.stopPropagation()} data-id="element-359">
              {/* Close Button */}
              <button onClick={() => setShowLocationModal(false)} className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-neutral-text/70 hover:text-neutral-text transition-colors focus-ring" aria-label="Close location finder" data-id="element-360">
                <XIcon className="w-5 h-5" data-id="element-361" />
              </button>

              {/* NearestLocationFinder embedded */}
              <NearestLocationFinder data-id="element-362" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <ShopFramesModal isOpen={showShopModal} onClose={() => setShowShopModal(false)} data-id="element-363" />
    </section>;
}